import React, { useCallback, useRef, useState, type ReactNode } from 'react';

export interface WindowFrameProps {
  title: string;
  active?: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
  icon?: ReactNode;
  onFocus: () => void;
  onClose?: () => void;
  onMinimize?: () => void;
  onMove: (x: number, y: number) => void;
  children: ReactNode;
}

/**
 * A draggable Win95 window frame.
 *
 * The frame is DOM rather than react-native-web: dragging needs pointer
 * capture and z-index stacking, neither of which RN models. Whatever is
 * rendered inside is the shared RN component tree, identical to iOS.
 */
export function WindowFrame({
  title,
  active = true,
  x,
  y,
  width,
  height,
  icon,
  onFocus,
  onClose,
  onMinimize,
  onMove,
  children,
}: WindowFrameProps) {
  const dragOffset = useRef<{ dx: number; dy: number } | null>(null);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      onFocus();
      // Never start a drag from the caption buttons. Capturing the pointer
      // retargets every later pointer event — including the click — to the
      // capturing element, so a captured drag silently swallowed the close and
      // minimise clicks and the buttons did nothing.
      if ((e.target as HTMLElement).closest('.win-buttons')) return;
      // Only a primary-button drag on the caption moves the window.
      if (e.button !== 0) return;
      dragOffset.current = { dx: e.clientX - x, dy: e.clientY - y };
      // Capture so the drag survives the cursor outrunning the title bar.
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [onFocus, x, y]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const off = dragOffset.current;
      if (!off) return;
      // Keep at least a sliver of the caption on screen so a window can always
      // be grabbed again.
      const nextX = Math.min(Math.max(e.clientX - off.dx, -width + 80), window.innerWidth - 60);
      const nextY = Math.min(Math.max(e.clientY - off.dy, 0), window.innerHeight - 60);
      onMove(nextX, nextY);
    },
    [onMove, width]
  );

  const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    dragOffset.current = null;
    const el = e.currentTarget as HTMLElement;
    if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
  }, []);

  return (
    <div
      className={`win raised ${active ? '' : 'inactive'}`}
      style={{ left: x, top: y, width, height }}
      onPointerDown={onFocus}
      role="dialog"
      aria-label={title}
    >
      <div className="inner" style={{ display: 'flex', flexDirection: 'column' }}>
        <div
          className="win-title"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          {icon ? <span aria-hidden>{icon}</span> : null}
          <span className="grab">{title}</span>
          <span className="win-buttons">
            {onMinimize ? (
              <button className="win-btn raised" onClick={onMinimize} aria-label={`Minimize ${title}`}>
                <span className="inner" style={{ display: 'grid', placeItems: 'end center' }}>
                  <span style={{ width: 6, height: 2, background: '#000', marginBottom: 2 }} />
                </span>
              </button>
            ) : null}
            {onClose ? (
              <button className="win-btn raised" onClick={onClose} aria-label={`Close ${title}`}>
                <span className="inner" style={{ display: 'grid', placeItems: 'center' }}>
                  <CloseGlyph />
                </span>
              </button>
            ) : null}
          </span>
        </div>
        <div className="win-body">{children}</div>
      </div>
    </div>
  );
}

/** The caption close mark, drawn rather than typed so it stays 1px crisp. */
function CloseGlyph() {
  return (
    <span style={{ position: 'relative', width: 8, height: 8, display: 'block' }}>
      <span style={strokeStyle('45deg')} />
      <span style={strokeStyle('-45deg')} />
    </span>
  );
}

const strokeStyle = (rotate: string): React.CSSProperties => ({
  position: 'absolute',
  top: 3,
  left: -1,
  width: 9,
  height: 1,
  background: '#000',
  transform: `rotate(${rotate})`,
});

/** Tracks window geometry and stacking for the desktop. */
export interface WindowState {
  id: string;
  title: string;
  x: number;
  y: number;
  width: number;
  height: number;
  minimized: boolean;
  z: number;
}

export function useWindowManager(initial: WindowState[] = []) {
  const [windows, setWindows] = useState<WindowState[]>(initial);
  const [focused, setFocused] = useState<string | null>(initial[0]?.id ?? null);
  const topZ = useRef(initial.length);

  const focus = useCallback((id: string) => {
    setFocused(id);
    setWindows((ws) => {
      const w = ws.find((x) => x.id === id);
      // Already on top, or minimized and being restored.
      if (w && w.z === topZ.current && !w.minimized) return ws;
      topZ.current += 1;
      const z = topZ.current;
      return ws.map((x) => (x.id === id ? { ...x, z, minimized: false } : x));
    });
  }, []);

  const open = useCallback(
    (w: Omit<WindowState, 'z' | 'minimized'>) => {
      setWindows((ws) => {
        if (ws.some((x) => x.id === w.id)) return ws;
        topZ.current += 1;
        return [...ws, { ...w, minimized: false, z: topZ.current }];
      });
      setFocused(w.id);
    },
    []
  );

  const close = useCallback((id: string) => {
    setWindows((ws) => ws.filter((w) => w.id !== id));
    setFocused((f) => (f === id ? null : f));
  }, []);

  const move = useCallback((id: string, x: number, y: number) => {
    setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, x, y } : w)));
  }, []);

  const minimize = useCallback((id: string) => {
    setWindows((ws) => ws.map((w) => (w.id === id ? { ...w, minimized: true } : w)));
    setFocused((f) => (f === id ? null : f));
  }, []);

  return { windows, setWindows, focused, focus, open, close, move, minimize };
}
